/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  INDIA — ITEMISED CONSENT REGISTRY (DPDP Act 2023 / DPDP Rules 2025)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 *  WHY THIS FILE EXISTS
 *  --------------------
 *  Before this, an Indian patient saw ONE checkbox ("Asia-Pacific PDPA") that
 *  bundled together: health-data processing, international transfer, AI
 *  processing and sharing with third parties. Under DPDP s.6(1) consent must be
 *  "free, specific, informed, unconditional and unambiguous with a clear
 *  affirmative action", and Rule 3 requires the notice to carry an ITEMISED
 *  description of the personal data and the SPECIFIC purpose for each use.
 *  Bundled consent of that kind is not valid consent, and it cannot be
 *  selectively withdrawn as s.6(4)–(6) requires.
 *
 *  This registry replaces the single checkbox with discrete, individually
 *  recordable and individually withdrawable consent items.
 *
 *  THE RULE THAT SHAPES EVERYTHING BELOW
 *  -------------------------------------
 *  DPDP s.6(1): consent is limited to "such personal data as is necessary for
 *  the specified purpose". So an item is only `required: true` when the service
 *  genuinely cannot be delivered without it. Anything that is a convenience, an
 *  enhancement or a commercial interest is optional and DEFAULTS TO OFF.
 *  Pre-ticked boxes are expressly prohibited.
 *
 *  ⚠ NOT LEGAL ADVICE. The engineering here implements a structure counsel can
 *    populate and sign off; the wording of each notice must be reviewed by an
 *    Indian healthcare/privacy practitioner before go-live.
 */

import { DATA_RESIDENCY, RETENTION, CLINICAL_GOVERNANCE } from './indiaConsentConfig';

/**
 * Consent bundle version.
 *
 * ⚠ OWNED BY THE BACKEND (spec rev 024c49a). This constant must always match
 * the `bundleVersion` the server stores and returns. It is NOT ours to bump
 * independently: `needsReconsent()` compares the server's
 * `acceptedBundleVersion` against this value, so if the two drift apart every
 * patient is re-prompted for consent on every single login.
 *
 * TO CHANGE IT: agree the new version with the backend team, deploy the server
 * change, then update this line. Never the other way round.
 *
 * DPDP requires proof of WHICH text a patient agreed to; the version travels in
 * the consent artefact, and a bump re-prompts patients who accepted older text.
 */
export const INDIA_CONSENT_BUNDLE_VERSION = 'v1.2';

export type IndiaConsentId =
  | 'health_data_processing'
  | 'telemedicine_care'
  | 'cross_border_transfer'
  | 'clinician_access'
  | 'ai_assisted_analysis'
  | 'session_recording'
  | 'share_with_treating_doctor'
  | 'service_improvement'
  | 'marketing_updates';

/** Where in the product the consent is collected. */
export type ConsentSurface = 'onboarding' | 'video_call' | 'documents' | 'settings';

export interface IndiaConsentItem {
  id: IndiaConsentId;
  version: string;
  /** true only where the service is impossible without it — see s.6(1) above. */
  required: boolean;
  surface: ConsentSurface;
  /** Short label rendered next to the checkbox. Must be self-contained. */
  label: string;
  /** One line under the label: what the patient is actually agreeing to. */
  summary: string;
  /** Rule 3 — itemised description of the personal data covered by THIS item. */
  dataItems: string[];
  /** Rule 3 — the specific purpose, stated as a purpose and not as a category. */
  purpose: string;
  /** Who receives the data under this item. */
  recipients: string[];
  /** Statutory hook, shown to the patient so the notice is genuinely informed. */
  legalBasis: string;
  /** DPDP s.6(4) — the patient must know what happens if they say no / withdraw. */
  consequenceOfDeclining: string;
  retention: string;
}

const CLINICIAN_RECIPIENTS = CLINICAL_GOVERNANCE.overseasReviewersInvolved
  ? [
    'The India-registered medical practitioner responsible for your case',
    'Specialist reviewers outside India who provide a non-prescriptive academic opinion',
    'Care coordinators employed by us who administer your case',
  ]
  : [
    'India-registered medical practitioners assigned to your case',
    'Care coordinators employed by us who administer your case',
  ];

const RETENTION_CLINICAL =
  `Retained for ${RETENTION.medicalRecordYears} years from the start of your care, as required by ` +
  `${RETENTION.medicalRecordBasis} After that period it is deleted or irreversibly anonymised.`;

/**
 * SURFACE ASSIGNMENT — why onboarding only carries the required four
 * -----------------------------------------------------------------
 * Presenting all nine at sign-up produced a wall of choices before the patient
 * had done anything, which is the classic consent-fatigue failure: people tick
 * to get past it, and consent obtained that way is not meaningfully informed.
 *
 * The ICO's layered / just-in-time guidance is the accepted answer, and it is
 * also better DPDP practice — s.6(1) ties consent to a specified purpose, and a
 * purpose is easiest to understand at the moment it arises. So each optional
 * consent now sits where it means something:
 *
 *   ai_assisted_analysis      → when documents are uploaded
 *   session_recording         → when a video call starts
 *   share_with_treating_doctor→ beside the treating-doctor fields
 *   service_improvement       → Consent & Privacy settings
 *   marketing_updates         → Consent & Privacy settings
 *
 * Nothing is hidden: every item stays visible and togglable in settings.
 */
export const INDIA_CONSENT_ITEMS: IndiaConsentItem[] = [
  // ─────────────────────────────────────────────────────────────────────────
  // REQUIRED — asked at onboarding; the service cannot exist without these
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'health_data_processing',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: true,
    surface: 'onboarding',
    label: 'Use my health information to prepare my second opinion',
    summary:
      'We collect and read the medical information you give us so a doctor can review your case and write your second-opinion report.',
    dataItems: [
      'Your name, date of birth, sex and contact details',
      'The medical history, symptoms and current treatment you describe to us',
      'Documents you upload — prescriptions, discharge summaries, lab reports, pathology reports',
      'Scans and imaging you upload, including CT, MRI, X-ray and ultrasound studies',
      'Messages you exchange with our doctors and care coordinators',
    ],
    purpose:
      'To review your medical records and produce a written second medical opinion for you, and to answer your follow-up questions about that opinion.',
    recipients: CLINICIAN_RECIPIENTS,
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023.',
    consequenceOfDeclining:
      'We cannot provide a second opinion without this. This is the core of the service.',
    retention: RETENTION_CLINICAL,
  },
  {
    id: 'telemedicine_care',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: true,
    surface: 'onboarding',
    label: 'Receive care from a doctor remotely (telemedicine)',
    summary:
      'You agree to be advised by a doctor over video, chat or written report rather than in person, and you understand the limits of that.',
    dataItems: [
      'A record that you consented to a teleconsultation, and when',
      'Consultation notes, written opinions and prescriptions issued to you',
      'Logs of the consultation — date, time, duration and who took part',
    ],
    purpose:
      'To let a Registered Medical Practitioner advise you remotely under the Telemedicine Practice Guidelines, 2020.',
    recipients: CLINICIAN_RECIPIENTS,
    legalBasis:
      'Telemedicine Practice Guidelines, 2020 (Appendix 5 to the IMC Regulations, 2002), which require your consent to a teleconsultation to be recorded. Where we or a doctor start the consultation, that consent must be explicit.',
    consequenceOfDeclining:
      'We cannot consult with you remotely. Our service is delivered entirely online, so it cannot proceed.',
    retention: RETENTION_CLINICAL,
  },
  {
    id: 'cross_border_transfer',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: true,
    surface: 'onboarding',
    // No interpolated brackets here: primaryRegionLabel already carries its own
    // ("Ireland (European Union)"), which nested into "India (Ireland (…))".
    // The destination is stated in the summary line directly below instead.
    label: 'Store and process my information outside India',
    summary:
      `Your records are held on secure servers in ${DATA_RESIDENCY.primaryRegionLabel}, not in India.`,
    dataItems: [
      'Everything you upload or tell us, including your health records and scans',
      'Your account and login information',
    ],
    purpose:
      `To host, secure and back up your records on our infrastructure located in ${DATA_RESIDENCY.primaryRegionLabel}, and to make them available to the clinicians reviewing your case.`,
    recipients: [
      `Our cloud hosting provider, operating in ${DATA_RESIDENCY.primaryRegionLabel}`,
      ...CLINICIAN_RECIPIENTS,
    ],
    legalBasis:
      'Section 16 of the Digital Personal Data Protection Act, 2023 permits transfer outside India except to countries restricted by the Central Government. No such restriction currently applies to this destination. We disclose the transfer to you because section 5 requires the notice to be complete.',
    consequenceOfDeclining:
      'We cannot deliver the service. Our platform runs outside India, so this cannot be turned off separately.',
    retention: RETENTION_CLINICAL,
  },
  {
    id: 'clinician_access',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: true,
    surface: 'onboarding',
    label: 'Let the doctors and coordinators handling my case see my records',
    summary:
      'Named clinicians and care coordinators assigned to you can open your file. Everyone else cannot.',
    dataItems: [
      'Your full case file, including uploaded documents and imaging',
      'Your messages and consultation history',
    ],
    purpose:
      'To let the specific clinicians and coordinators assigned to your case read your records so they can review, discuss and answer questions about your case.',
    recipients: CLINICIAN_RECIPIENTS,
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023, together with the confidentiality duty in Regulation 7.14 of the IMC Regulations, 2002.',
    consequenceOfDeclining:
      'No clinician could open your file, so no opinion could be produced.',
    retention: RETENTION_CLINICAL,
  },

  // ─────────────────────────────────────────────────────────────────────────
  // OPTIONAL — default OFF, individually withdrawable
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: 'ai_assisted_analysis',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: false,
    // Asked when documents are uploaded — the moment the AI would actually read
    // something — not buried in a list of nine choices before onboarding starts.
    surface: 'documents',
    label: 'Allow AI tools to help summarise my records and draft my report',
    summary:
      'Software prepares a first draft and a summary of your documents. A doctor reviews and is responsible for everything sent to you. You can say no and still get the full service.',
    dataItems: [
      'The text of documents you upload',
      'Your medical history and current treatment details',
      'Dictated clinical notes recorded by your doctor about your case',
    ],
    purpose:
      'To automatically summarise your uploaded records and produce a first draft of your report for your doctor to check, correct and approve.',
    recipients: [
      'Our AI processing provider, under contractual confidentiality and no-training obligations',
      'The clinician reviewing your case, who checks and signs off the output',
    ],
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023, applying the ICMR Ethical Guidelines for Application of Artificial Intelligence in Biomedical Research and Healthcare, 2023, which require that you keep the choice to refuse AI-assisted handling and that a human clinician remains accountable.',
    consequenceOfDeclining:
      'Nothing is lost. Your doctor reviews and writes your opinion without AI assistance. It may take a little longer.',
    retention: RETENTION_CLINICAL,
  },
  {
    id: 'session_recording',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: false,
    surface: 'video_call',
    label: 'Record my video consultation',
    summary:
      'The video and audio of your consultation is saved so you and your doctor can go back to it. Off unless you turn it on.',
    dataItems: [
      'Video and audio of the consultation',
      'Anything visible or spoken during the call',
    ],
    purpose:
      'To keep a recording of the consultation so that you and your treating clinicians can refer back to what was said.',
    recipients: [
      'You',
      'The clinicians assigned to your case',
    ],
    legalBasis:
      'Telemedicine Practice Guidelines, 2020, which require the practitioner to take your explicit informed consent before any audio or video recording of a consultation.',
    consequenceOfDeclining:
      'Your consultation goes ahead exactly as normal and is simply not recorded.',
    retention:
      `Recordings are kept for ${RETENTION.medicalRecordYears} years alongside your medical record, and deleted immediately if you withdraw this consent.`,
  },
  {
    id: 'share_with_treating_doctor',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: false,
    // Belongs beside the field where the patient names their doctor.
    surface: 'settings',
    label: 'Send my second-opinion report to my own doctor',
    summary:
      'We email the finished report to the treating doctor whose details you give us.',
    dataItems: [
      'Your finished second-opinion report',
      'The name, email and phone number of the doctor you nominate',
    ],
    purpose:
      'To send your completed report to the treating doctor you nominate, so they can factor it into your ongoing care.',
    recipients: ['The treating doctor or hospital you name'],
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023.',
    consequenceOfDeclining:
      'We do not contact your doctor. You can still download your report and share it yourself.',
    retention:
      'The nominated doctor\'s contact details are kept only while your case is open, then deleted.',
  },
  {
    id: 'service_improvement',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: false,
    surface: 'settings',
    label: 'Use de-identified information from my case to improve the service',
    summary:
      'Your name and identifying details are stripped out first. Never used to train AI models unless you separately agree.',
    dataItems: [
      'De-identified clinical findings from your case, with direct identifiers removed',
      'Anonymous usage statistics about how the service performed',
    ],
    purpose:
      'To audit the quality of the opinions we produce and improve how the service works.',
    recipients: ['Our internal clinical governance and quality team'],
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023, applying the anonymisation expectations in the ICMR Ethical Guidelines, 2023.',
    consequenceOfDeclining:
      'No effect at all on your care or your report.',
    retention:
      'De-identified records are kept without a fixed end date. They cannot be traced back to you.',
  },
  {
    id: 'marketing_updates',
    version: INDIA_CONSENT_BUNDLE_VERSION,
    required: false,
    surface: 'settings',
    label: 'Send me health tips and updates about new services',
    summary:
      'Marketing only. Nothing to do with your care. You will still receive messages about your own case either way.',
    dataItems: ['Your name, email address and phone number'],
    purpose:
      'To send you promotional messages about our services. Not used for anything clinical.',
    recipients: ['Our marketing communications provider'],
    legalBasis:
      'Your consent under section 6 of the Digital Personal Data Protection Act, 2023, and TRAI\'s Telecom Commercial Communications Customer Preference Regulations, 2018 for SMS and voice.',
    consequenceOfDeclining:
      'You receive no marketing. Messages about your appointments, reports and payments continue, because those are part of the service you asked for.',
    retention: 'Kept until you unsubscribe or withdraw this consent.',
  },
];

// ── Lookups ────────────────────────────────────────────────────────────────

export const getIndiaConsentItem = (id: IndiaConsentId): IndiaConsentItem | undefined =>
  INDIA_CONSENT_ITEMS.find((i) => i.id === id);

export const getIndiaConsentsForSurface = (surface: ConsentSurface): IndiaConsentItem[] =>
  INDIA_CONSENT_ITEMS.filter((i) => i.surface === surface);

export const REQUIRED_INDIA_CONSENT_IDS: IndiaConsentId[] = INDIA_CONSENT_ITEMS
  .filter((i) => i.required)
  .map((i) => i.id);

/**
 * Default state for a fresh consent form.
 *
 * Every box starts FALSE — including the required ones. DPDP Rules 2025 Rule 3
 * prohibits pre-ticked boxes, and "required" here means "the service stops
 * without it", never "assume yes".
 */
export const buildInitialConsentState = (
  items: IndiaConsentItem[] = INDIA_CONSENT_ITEMS,
): Record<string, boolean> =>
  items.reduce<Record<string, boolean>>((acc, item) => {
    acc[item.id] = false;
    return acc;
  }, {});

/** All mandatory items for a surface accepted? */
export const areRequiredConsentsGiven = (
  state: Record<string, boolean>,
  surface: ConsentSurface = 'onboarding',
): boolean =>
  getIndiaConsentsForSurface(surface)
    .filter((i) => i.required)
    .every((i) => state[i.id] === true);
